// The three light screens — one component, switched by `variant`:
//
//   'rgb'    rgbScreen.dart   (DeviceID 24) colour wheel · preview dot ·
//                             R/G/B sliders · Red Blue Green · Off
//   'wwh'    wwhScreen.dart   (DeviceID 25) two vertical "water" sliders ·
//                             White · Warm White · Cool White · Off
//   'wwrgb'  wwrgbScreen.dart (DeviceID 23) white + warm sliders · colour wheel ·
//                             Red Blue Green · White · Off
//
// Channel order and preset payloads come from core/devices/light.js — note the
// WWRGB payloads lead with white and warm, so "Red" is 0,0,255,0,0.

import React, { useMemo } from 'react';
import { useContentHeight } from '../../components/MobileShell';
import { Box, useTheme } from '@mui/material';
import DeviceAppBar from './components/DeviceAppBar';
import ColorArea from './components/ColorArea';
import {
  ApplyingOverlay, ChannelSlider, PresetButton, VerticalSlider,
} from './components/LightBits';
import { useApp } from '../../../../core/state/AppContext';
import { useLightChannels } from '../../../../core/hooks/useLightChannels';
import {
  RGB_PRESETS, WWH_PRESETS, WWRGB_PRESETS, channelCount, rgbCss,
} from '../../../../core/devices/light';

const PRESETS = { rgb: RGB_PRESETS, wwh: WWH_PRESETS, wwrgb: WWRGB_PRESETS };

// The five WWRGB channels in wire order — white, warm white, R, G, B. Same
// order as the MQTT payload, so the slider index IS the channel index.
const WWRGB_CHANNELS = [
  { label: 'W', color: '#FFFFFF' },
  { label: 'WW', color: '#FFD08A' },
  { label: 'R', color: '#F44336' },
  { label: 'G', color: '#39FF00' },
  { label: 'B', color: '#2196F3' },
];

export default function LightScreen({ device, variant }) {
  const theme = useTheme();
  const contentH = useContentHeight();
  const c = theme.sli;
  const { userId } = useApp();
  const uno = device?.UniqueNumber;

  const count = channelCount(variant);
  const {
    values, setValues, applying, commit, sendCommand, setAndDebounce,
  } = useLightChannels(uno, count, userId, variant === 'rgb' ? 3000 : 3000);

  // Where the R/G/B triple sits inside this device's channel list.
  const rgbOffset = variant === 'wwrgb' ? 2 : 0;
  // Only RGB gets the colour wheel now — WWRGB drives all five channels from its
  // vertical sliders instead, so the wheel, the preview dot and the horizontal
  // R/G/B sliders below are skipped there.
  const hasColour = variant === 'rgb';
  const rgb = hasColour
    ? [values[rgbOffset] || 0, values[rgbOffset + 1] || 0, values[rgbOffset + 2] || 0]
    : [0, 0, 0];

  // Colour-wheel size (RGB only — WWRGB has no wheel, see below). Derived from
  // both axes like the Dart, so it can't balloon on a tablet.
  const pickerWidth = useMemo(() => {
    const availW = window.innerWidth;
    // ⚠️ THE CONTENT AREA, NOT THE WINDOW. The window also holds the identity
    // strip and the company footer, which this screen does not get — sizing
    // from it overflows by exactly their height, and the overflow is clipped
    // rather than scrolled. See ui/mobile/components/MobileShell.
    const availH = contentH - 64;
    return Math.min(420, Math.max(180, Math.min(availW * 0.9, availH * 1.1)));
  }, [contentH]);

  // WWRGB: how tall the five channel sliders can be and still leave room for
  // their labels and the two rows of presets underneath. Everything must fit
  // WITHOUT scrolling — that is the whole point of this layout.
  //
  // The 300 reserve is measured, not guessed: page padding 32 + slider labels 20
  // + spacer 16 + two preset rows (56 swatch + ~20 label + 12 gap) × 2 ≈ 176 +
  // bottom padding 20.
  const vSliderHeight = useMemo(() => {
    // ⚠️ THE CONTENT AREA, NOT THE WINDOW. The window also holds the identity
    // strip and the company footer, which this screen does not get — sizing
    // from it overflows by exactly their height, and the overflow is clipped
    // rather than scrolled. See ui/mobile/components/MobileShell.
    const availH = contentH - 64;
    return Math.round(Math.min(280, Math.max(120, availH - 300)));
  }, [contentH]);

  const setChannel = (index, value) => {
    const next = [...values];
    next[index] = value;
    setValues(next);
  };

  const onWheel = (next) => {
    const merged = [...values];
    merged[rgbOffset] = next[0];
    merged[rgbOffset + 1] = next[1];
    merged[rgbOffset + 2] = next[2];
    setAndDebounce(merged);
  };

  return (
    <Box sx={{ height: '100%', bgcolor: c.back, display: 'flex', flexDirection: 'column' }}>
      <DeviceAppBar device={device} />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 2 }}>
        <Box sx={{
          maxWidth: 480, mx: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center',
        }}
        >
          {/* WWH: the two white channels as upright sliders, side by side. */}
          {variant === 'wwh' && (
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 6, pt: 2, pb: 3 }}>
              <VerticalSlider
                label="White"
                value={values[0] || 0}
                activeColor="#FFFFFF"
                onChange={(v) => setChannel(0, v)}
                onCommit={() => commit()}
              />
              <VerticalSlider
                label="Warm White"
                value={values[1] || 0}
                activeColor="#FFD08A"
                onChange={(v) => setChannel(1, v)}
                onCommit={() => commit()}
              />
            </Box>
          )}

          {/* WWRGB: all five channels as upright sliders in one row, the way the
              desktop card shows them, with the presets below — everything on one
              screen and nothing below the fold.

              This drops the colour wheel that the Dart's wwrgbScreen has. Five
              horizontal sliders plus a wheel, a preview dot and five preset
              buttons simply do not fit a phone: the buttons ended up off-screen,
              which is what prompted the change. The wheel stays on the RGB
              screen, which has the room for it. */}
          {variant === 'wwrgb' && (
            <Box sx={{
              display: 'grid',
              // Five equal columns, so the sliders are evenly spaced and their
              // labels line up regardless of screen width.
              gridTemplateColumns: 'repeat(5, 1fr)',
              justifyItems: 'center',
              alignItems: 'end',
              width: '100%',
              pt: 1,
              pb: 2,
            }}
            >
              {WWRGB_CHANNELS.map((ch, i) => (
                <VerticalSlider
                  key={ch.label}
                  label={ch.label}
                  height={vSliderHeight}
                  value={values[i] || 0}
                  activeColor={ch.color}
                  onChange={(v) => setChannel(i, v)}
                  onCommit={() => commit()}
                />
              ))}
            </Box>
          )}

          {hasColour && (
            <>
              <ColorArea rgb={rgb} onChange={onWheel} width={pickerWidth} />

              {/* Blended preview dot. */}
              <Box sx={{
                width: 80,
                height: 80,
                mt: 2,
                borderRadius: '50%',
                bgcolor: rgbCss(rgb[0], rgb[1], rgb[2]),
                boxShadow: '0 4px 8px rgba(0,0,0,0.26)',
              }}
              />
              <Box sx={{ height: 16 }} />

              <Box sx={{ width: '100%', px: 1 }}>
                <ChannelSlider
                  value={rgb[0]}
                  activeColor="#F44336"
                  onChange={(v) => setChannel(rgbOffset, v)}
                  onCommit={() => commit()}
                />
                <Box sx={{ height: 16 }} />
                <ChannelSlider
                  value={rgb[1]}
                  activeColor="#39FF00"
                  onChange={(v) => setChannel(rgbOffset + 1, v)}
                  onCommit={() => commit()}
                />
                <Box sx={{ height: 16 }} />
                <ChannelSlider
                  value={rgb[2]}
                  activeColor="#0000C8"
                  onChange={(v) => setChannel(rgbOffset + 2, v)}
                  onCommit={() => commit()}
                />
              </Box>
            </>
          )}

          {/* Presets.
              WWRGB has six (White · Warm White · Red · Green · Blue · Off) and
              uses a fixed 3-column grid — two tidy rows of three, columns and
              labels aligned, instead of a wrap that reflows differently on every
              screen width. The other variants keep Flutter's centred wrap. */}
          <Box sx={variant === 'wwrgb' ? {
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            justifyItems: 'center',
            alignItems: 'start',
            rowGap: 1.5,
            columnGap: 1,
            width: '100%',
            maxWidth: 320,
            pb: 2.5,
          } : {
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 5,
            mt: 3.75,
            pb: 2.5,
          }}
          >
            {PRESETS[variant].map((p) => (
              <PresetButton
                key={p.label}
                label={p.label}
                swatch={p.swatch}
                size={variant === 'wwrgb' ? 56 : 70}
                onClick={() => sendCommand(p.command)}
              />
            ))}
          </Box>
        </Box>
      </Box>

      <ApplyingOverlay open={applying} />
    </Box>
  );
}
